import { RoutingPlan, RoutingPlans } from 'schemas/RoutingPlan.schema';
import { NotifyRoutingPlan, notifyRoutingPlansSchema } from 'schemas/NotifyRoutingPlan.schema';
import { DateTime } from 'luxon';
import Papa from 'papaparse';
import { IterationRule } from 'schemas/IterationRule.schema';
import { ZodSafeParseResult } from 'zod';

export function getRoutingPlanId(routingPlanId: string, version: number | undefined): string {
  if (version === undefined) {
    return routingPlanId;
  }

  return `${routingPlanId}:${version}`;
}

export function getLatestRoutingPlans(plans: RoutingPlans): RoutingPlan[] {
  return Object.values(
    plans.reduce<Record<string, RoutingPlan>>((acc, plan) => {
      const existing = acc[plan.routing_plan_id];
      if (
        !existing
        || (plan.version !== undefined
          && (existing.version === undefined || plan.version > existing.version))
      ) {
        acc[plan.routing_plan_id] = plan;
      }
      return acc;
    }, {}),
  );
}

export async function parseRoutingPlansFromClipboard(): Promise<ZodSafeParseResult<NotifyRoutingPlan[]>> {
  const textFromClipboard = await navigator.clipboard.readText();

  if (!textFromClipboard.trim()) {
    throw new Error('Clipboard is empty');
  }

  const { data, errors } = Papa.parse<Record<string, string>>(textFromClipboard, {
    header: true,
    // Trim whitespace and remove surrounding quotes from each field
    transform: (value) => value.trim().replace(/^"(.*)"$/, '$1').trim(),
  });

  if (errors.length > 0) {
    throw new Error(`Failed to parse CSV: ${errors[0].message}`);
  }

  return notifyRoutingPlansSchema.safeParse(data);
}

export function getRoutingPlanName(teamRoutingPlans: RoutingPlans | undefined, id: string | undefined): string {
  if (!id) {
    return '';
  }

  return teamRoutingPlans?.find((plan) => `${plan.routing_plan_id}:${plan.version}` === id)?.routing_plan_name || id;
}

export function getCommsTarget(teamRoutingPlans: RoutingPlans | undefined, id: string | undefined): string {
  if (!id) {
    return '';
  }

  return teamRoutingPlans?.find((plan) => `${plan.routing_plan_id}:${plan.version}` === id)?.comms_target || '';
}

export function findRoutingPlan(routingID: string, teamRoutingPlans: RoutingPlans): RoutingPlan | undefined {
  return teamRoutingPlans.find(
    (plan) => getRoutingPlanId(plan.routing_plan_id, plan.version) === routingID,
  );
}

export interface RoutingPlanNameAndTarget {
  name: string;
  target: string | null;
}

export function getRoutingPlanNamesAndTargets(teamRoutingPlans: RoutingPlans | undefined, routingPlanIds: string | undefined): RoutingPlanNameAndTarget[] {
  if (!routingPlanIds) {
    return [];
  }

  return routingPlanIds.split('|').map((id) => {
    const plan = teamRoutingPlans?.find((p) => `${p.routing_plan_id}:${p.version}` === id);
    return { name: plan?.routing_plan_name || id, target: plan?.comms_target ?? null };
  });
}

export function stripRoutingPlanVersions(routingPlanIds: string): string {
  return routingPlanIds
    .split(',')
    .map((id) => id.trim().split(':')[0])
    .filter((id) => id.length > 0)
    .join(',');
}

export function hasOnlyUnversionedRoutingPlans(routingPlanIds: string[] | undefined): boolean {
  if (routingPlanIds === undefined || routingPlanIds.length === 0) {
    return false;
  }

  return !(routingPlanIds?.some((id) => id.split(':').length > 1) ?? false);
}

export const applyAgeFilter = (routingPlans: RoutingPlans, maxAge: number): RoutingPlans => {
  if (!Number.isNaN(maxAge)) {
    const cutOff = DateTime.now()
      .minus({ months: maxAge })
      .toMillis()
      .toString();

    return [...routingPlans].filter(
      (plan) => plan.created_at !== undefined && plan.created_at >= cutOff,
    );
  }

  return [...routingPlans];
};

export function applyWordsFilter(teamRoutingPlans: RoutingPlans, filterArray: string[]): RoutingPlans {
  const newRoutingPlans = [...teamRoutingPlans];

  return newRoutingPlans.filter((plan) => filterArray.every((filter) => {
    if (filter.startsWith('-')) {
      return !plan.routing_plan_name.toLowerCase().includes(filter.slice(1));
    }
    return plan.routing_plan_name.toLowerCase() === filter || plan.routing_plan_name.toLowerCase().includes(filter);
  }));
}

export const getFilteredRoutingPlans = ({ routingPlans, maxAge, wordsFilter }:
    { routingPlans: RoutingPlans, maxAge: number, wordsFilter: string
    }): RoutingPlans => {
  const ageFiltered = applyAgeFilter(routingPlans, maxAge);
  const trimmed = wordsFilter.trim();
  const hasWordFilter = trimmed.length > 0 && trimmed !== '-';

  if (hasWordFilter) {
    return applyWordsFilter(ageFiltered, trimmed.toLowerCase().split(' '));
  }

  return ageFiltered;
};

export interface VersionedRoutingPlan {
  status: 'outdated' | 'latest';
  routingPlan: RoutingPlan;
}

export function getOldRoutingPlanVersions(
  entry: string,
  teamRoutingPlans: RoutingPlans,
  latestPlans: RoutingPlan[],
): VersionedRoutingPlan[] {
  const [guid, versionStr] = entry.split(':');
  const version = versionStr !== undefined ? Number(versionStr) : undefined;

  const matched = teamRoutingPlans.find(
    (plan) => plan.routing_plan_id === guid && plan.version === version,
  );

  if (!matched) {
    return [];
  }

  const latest = latestPlans.find((plan) => plan.routing_plan_id === guid);
  const isOutdated = latest !== undefined && latest.version !== matched.version;

  if (isOutdated) {
    return [
      { status: 'outdated' as const, routingPlan: matched },
      { status: 'latest' as const, routingPlan: latest },
    ];
  }

  return [];
}

export function getOldRoutingPlans(
  teamRoutingPlans: RoutingPlans | undefined,
  defaultCommsRouting: string | undefined,
): VersionedRoutingPlan[] {
  if (!teamRoutingPlans) {
    return [];
  }
  if (!defaultCommsRouting) {
    return [];
  }

  const latestPlans = getLatestRoutingPlans(teamRoutingPlans);

  return defaultCommsRouting
    .split('|')
    .flatMap((entry) => getOldRoutingPlanVersions(entry, teamRoutingPlans, latestPlans));
}

export function getOldRoutingRules(
  teamRoutingPlans: RoutingPlans | undefined,
  routingRules: IterationRule[] | undefined,
): VersionedRoutingPlan[] {
  if (!teamRoutingPlans) {
    return [];
  }
  if (!routingRules) {
    return [];
  }

  const latestPlans = getLatestRoutingPlans(teamRoutingPlans);

  return routingRules
    .filter((entry) => !!entry.CommsRouting)
    .flatMap((entry) => entry.CommsRouting!.split('|'))
    .flatMap((commsRouting) => getOldRoutingPlanVersions(commsRouting, teamRoutingPlans, latestPlans));
}

export function getOldRoutingRule(
  teamRoutingPlans: RoutingPlans | undefined,
  commsRouting: string,
): VersionedRoutingPlan[] {
  if (!teamRoutingPlans) {
    return [];
  }

  const latestPlans = getLatestRoutingPlans(teamRoutingPlans);
  return commsRouting
    .split('|')
    .flatMap((entry) => getOldRoutingPlanVersions(entry, teamRoutingPlans, latestPlans));
}

export function getLatestRoutingPlansString(
  teamRoutingPlans: RoutingPlans | undefined,
  routingPlansString: string | undefined,
): string {
  if (!routingPlansString) {
    return '';
  }

  if (!teamRoutingPlans) {
    return routingPlansString;
  }

  const latestPlans = getLatestRoutingPlans(teamRoutingPlans);

  return routingPlansString
    .split('|')
    .flatMap((routingPlan) => {
      const resolved = getOldRoutingPlanVersions(routingPlan, teamRoutingPlans, latestPlans);

      if (resolved.length === 0) {
        return [routingPlan];
      }

      return resolved
        .filter((item) => item.status === 'latest')
        .map((item) => `${item.routingPlan.routing_plan_id}:${item.routingPlan.version}`);
    })
    .join('|');
}

export function resolveRoutingIds(
  availableRoutingIds: string[],
  teamRoutingPlans: RoutingPlans | undefined,
): string[] {
  if (!teamRoutingPlans) return availableRoutingIds;
  const latestTeamRoutingPlanVersions = getLatestRoutingPlans(teamRoutingPlans);
  return availableRoutingIds.map((id) => {
    if (!id.includes(':')) {
      const latestTeamRoutingPlan = latestTeamRoutingPlanVersions.find((plan) => plan.routing_plan_id === id);
      if (latestTeamRoutingPlan?.version) return `${latestTeamRoutingPlan.routing_plan_id}:${latestTeamRoutingPlan.version}`;
    }
    return getLatestRoutingPlansString(teamRoutingPlans, id) || id;
  });
}

export function getLatestRoutingPlansArray(
  teamRoutingPlans: RoutingPlans | undefined,
  routingPlanIds: string[] | undefined,
): string[] | undefined {
  if (routingPlanIds === undefined) {
    return undefined;
  }

  if (routingPlanIds.length === 0) {
    return [];
  }

  if (!teamRoutingPlans) {
    return routingPlanIds;
  }

  const latestPlans = getLatestRoutingPlans(teamRoutingPlans);

  return routingPlanIds.flatMap((routingPlan) => {
    const resolved = getOldRoutingPlanVersions(routingPlan, teamRoutingPlans, latestPlans);

    if (resolved.length === 0) {
      return [routingPlan];
    }

    return resolved
      .filter((item) => item.status === 'latest')
      .map((item) => `${item.routingPlan.routing_plan_id}:${item.routingPlan.version}`);
  });
}

export const NOTIFY_TO_SPI_STATUS_MAP: Record<NotifyRoutingPlan['status'], RoutingPlan['status']> = {
  DRAFT: 'draft',
  COMPLETED: 'production',
  LEGACY: 'legacy',
};

export function mapNotifyRoutingPlansToSpi(plans: NotifyRoutingPlan[], existingPlans: RoutingPlans): RoutingPlans {
  return plans.map((plan) => {
    const foundExistingPlan = existingPlans
      .filter((p) => p.routing_plan_id === plan.routing_plan_id)
      .toSorted((a, b) => ((b.created_at ?? 0) > (a.created_at ?? 0) ? 1 : -1)).at(0);

    if (foundExistingPlan) {
      return {
        ...plan,
        comms_target: foundExistingPlan.comms_target,
        status: NOTIFY_TO_SPI_STATUS_MAP[plan.status],
      };
    }

    return {
      ...plan,
      comms_target: null,
      status: NOTIFY_TO_SPI_STATUS_MAP[plan.status],
    };
  });
}
